<template>
  <div class="tabulation-sheet-view">
    <!-- Header Info Section -->
    <b-card class="view-header-card">
      <div class="view-header">
        <div class="title-wrapper">
          <div class="d-flex align-items-center flex-wrap">
            <h4 class="mb-0 mr-1">Tabulation Sheet Details</h4>
            <b-badge
              v-if="!hidesTabulationTypePicker"
              variant="light-primary"
              class="text-uppercase badge-pill"
            >
              {{ resolvedTypeDisplayName }}
            </b-badge>
          </div>
          <small class="text-muted">
            {{ classInfo || 'Class' }} · {{ subjectInfo || 'Subject' }}
          </small>
        </div>
      </div>

      <div class="view-meta mt-1">
        <div class="meta-item meta-item--class">
          <span class="label">Class</span>
          <div v-if="classNamesList.length" class="class-tags">
            <b-badge
              v-for="(name, index) in classNamesList"
              :key="`class-tag-${index}-${name}`"
              variant="light-primary"
              class="class-tag"
            >
              {{ name }}
            </b-badge>
          </div>
          <span v-else class="value">{{ classInfo || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Section</span>
          <span class="value">{{ sectionDisplay }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Subject</span>
          <span class="value">{{ subjectInfo || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Exam Type</span>
          <span class="value">{{ examTypeInfo || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Session</span>
          <span class="value">{{ sessionInfo || 'N/A' }}</span>
        </div>
        <div v-if="!hidesTabulationTypePicker" class="meta-item">
          <span class="label">Type</span>
          <span class="value text-uppercase">{{
            resolvedTypeDisplayName || "N/A"
          }}</span>
        </div>
      </div>
    </b-card>

    <!-- Content Section -->
    <div class="view-content-wrapper">
      <!-- Icon Base View -->
      <template v-if="type === 'icon-base'">
        <!-- Profectus: full-width section-by-section layout -->
        <template v-if="isRemarksBaseSchool">
          <div v-if="loading" class="profectus-remarks-view">
            <div
              v-for="n in 2"
              :key="`profectus-skeleton-${n}`"
              class="profectus-section-block skeleton-card"
            >
              <div class="profectus-section-header skeleton-category-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-category-title"></div>
              </div>
              <div class="profectus-options-table-wrap">
                <div
                  v-for="m in 5"
                  :key="m"
                  class="skeleton-option-row profectus-skeleton-row"
                ></div>
              </div>
            </div>
          </div>

          <div v-else class="profectus-remarks-view">
            <div
              v-for="(category, categoryIndex) in categories"
              :key="categoryIndex"
              class="profectus-section-block"
            >
              <div class="profectus-section-header">
                <div class="profectus-section-icon">
                  <feather-icon icon="FolderIcon" size="22" />
                </div>
                <div class="profectus-section-title">
                  <h5 class="mb-0 text-uppercase">
                    {{ category.categoryName || "Unnamed Category" }}
                  </h5>
                  <small class="text-muted">
                    Section {{ category.sortIndex || categoryIndex + 1 }} ·
                    {{ (category.options || []).length }} criteria
                  </small>
                </div>
                <div class="profectus-section-meta">
                  <div
                    v-if="category.bothRemarks === true"
                    class="view-lang-inline-group"
                  >
                    <span
                      v-for="lang in remarksLanguageOptions"
                      :key="`view-lang-${categoryIndex}-${lang.code}`"
                      class="view-lang-box active"
                    >
                      <span class="view-lang-box-code">{{ lang.code }}</span>
                      <span class="view-lang-box-label">{{ lang.shortLabel }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="category.options && category.options.length"
                class="profectus-options-table-wrap"
              >
                <table class="profectus-options-table">
                  <tbody>
                    <tr
                      v-for="(option, optionIndex) in category.options"
                      :key="optionIndex"
                    >
                      <td class="col-sno">
                        {{ option.sortIndex || optionIndex + 1 }}
                      </td>
                      <td>{{ option.optionName || "Unnamed Option" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="view-empty-state">
                <feather-icon icon="InboxIcon" size="32" />
                <span>No options available</span>
              </div>
            </div>

            <div v-if="categories.length === 0" class="view-empty-state-large">
              <feather-icon icon="FolderIcon" size="48" />
              <h5>No categories available</h5>
              <p>This tabulation sheet doesn't have any categories yet.</p>
            </div>
          </div>
        </template>

        <!-- Default icon-base layout -->
        <template v-else>
        <!-- Loading Skeleton -->
        <div v-if="loading" class="view-icon-base-grid">
          <div v-for="n in 3" :key="n" class="view-category-card-modern skeleton-card">
            <div class="skeleton-category-header">
              <div class="skeleton-icon"></div>
              <div class="skeleton-category-title"></div>
            </div>
            <div class="skeleton-options">
              <div v-for="m in 4" :key="m" class="skeleton-option-row"></div>
            </div>
          </div>
        </div>
        
        <!-- Content -->
        <div v-else class="view-icon-base-grid">
          <div
            v-for="(category, categoryIndex) in categories"
            :key="categoryIndex"
            class="view-category-card-modern"
          >
            <div class="view-category-header-modern">
              <div class="view-category-icon">
                <feather-icon icon="FolderIcon" size="24" />
              </div>
              <div class="title-block">
                <h6 class="mb-0 text-uppercase">
                  {{ category.categoryName || 'Unnamed Category' }}
                </h6>
                <small class="text-muted">Sort {{ category.sortIndex || '-' }}</small>
              </div>
              <b-badge variant="light-primary" pill>
                {{ (category.options || []).length }} Options
              </b-badge>
            </div>
            <div class="category-divider" />
            <div v-if="category.options && category.options.length" class="options-list">
              <div
                v-for="(option, optionIndex) in category.options"
                :key="optionIndex"
                class="option-row"
                :style="{ animationDelay: `${(categoryIndex * 0.1) + (optionIndex * 0.05)}s` }"
              >
                <div class="option-label">
                  <span class="chip-index">{{ option.sortIndex || optionIndex + 1 }}</span>
                  <span class="chip-label">{{ option.optionName || 'Unnamed Option' }}</span>
                </div>
              </div>
            </div>
            <div v-else class="view-empty-state">
              <feather-icon icon="InboxIcon" size="32" />
              <span>No options available</span>
            </div>
          </div>
          <div v-if="categories.length === 0" class="view-empty-state-large">
            <feather-icon icon="FolderIcon" size="48" />
            <h5>No categories available</h5>
            <p>This tabulation sheet doesn't have any categories yet.</p>
          </div>
        </div>
        </template>
      </template>

      <!-- Questions Base / Sections View -->
      <template v-else-if="type === 'questions-base'">
        <div class="view-questions-wrapper">
          <div class="view-questions-summary">
            <div class="view-summary-card">
              <feather-icon icon="AwardIcon" size="24" class="text-primary" />
              <div class="view-summary-content">
                <span class="view-summary-label">Total Marks</span>
                <span class="view-summary-value">{{ totalMarks }}</span>
              </div>
            </div>
            <div class="view-summary-card">
              <feather-icon icon="ListIcon" size="24" class="text-primary" />
              <div class="view-summary-content">
                <span class="view-summary-label">{{
                  isSectionsMode ? "Total Sections" : "Total Questions"
                }}</span>
                <span class="view-summary-value">{{ records.length }}</span>
              </div>
            </div>
          </div>
          <div class="view-questions-list-modern">
            <div
              v-for="(record, recordIndex) in records"
              :key="recordIndex"
              class="view-question-wrapper"
            >
              <div class="view-question-card-modern">
                <div class="view-question-number-badge">
                  {{ recordIndex + 1 }}
                </div>
                <div class="view-question-details">
                  <div class="view-question-title-inline">
                    {{
                      record.questionNumber ||
                        (isSectionsMode
                          ? `Section ${recordIndex + 1}`
                          : recordIndex + 1)
                    }}
                  </div>
                  <div class="view-question-marks-badge">
                    <feather-icon icon="StarIcon" size="14" />
                    {{ record.totalMarks || 0 }} Marks
                  </div>
                </div>
              </div>
            </div>
            <div v-if="records.length === 0" class="view-empty-state-large">
              <feather-icon icon="FileTextIcon" size="48" />
              <h5>
                {{
                  isSectionsMode
                    ? "No sections available"
                    : "No questions available"
                }}
              </h5>
              <p>
                {{
                  isSectionsMode
                    ? "This tabulation sheet doesn't have any sections yet."
                    : "This tabulation sheet doesn't have any questions yet."
                }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- Class Test View -->
      <template v-else-if="type === 'class-test'">
        <div class="view-class-test-grid">
          <div
            v-for="(record, recordIndex) in records"
            :key="recordIndex"
            class="view-class-test-card-modern"
          >
            <div class="view-test-header-modern">
              <feather-icon icon="FileIcon" size="20" />
              <h5 class="view-test-title-modern">{{ record.name || 'Unnamed Test' }}</h5>
            </div>
            <div class="view-test-stats">
              <div class="view-test-stat-item">
                <feather-icon icon="HelpCircleIcon" size="16" />
                <span class="view-test-stat-label">Questions</span>
                <span class="view-test-stat-value">{{ record.totalQuestion || 0 }}</span>
              </div>
              <div class="view-test-stat-item">
                <feather-icon icon="StarIcon" size="16" />
                <span class="view-test-stat-label">Marks</span>
                <span class="view-test-stat-value">{{ record.totalMarks || 0 }}</span>
              </div>
            </div>
          </div>
          <div v-if="records.length === 0" class="view-empty-state-large">
            <feather-icon icon="FileIcon" size="48" />
            <h5>No tests available</h5>
            <p>This tabulation sheet doesn't have any tests yet.</p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { BBadge, BCard } from 'bootstrap-vue'

const REMARKS_LANGUAGE_OPTIONS = [
  { code: 'E', key: 'english', shortLabel: 'English' },
  { code: 'U', key: 'urdu', shortLabel: 'Urdu' },
]

export default {
  name: 'TabulationSheetView',
  components: {
    BBadge,
    BCard,
  },
  data() {
    return {
      hasViewRights: true, // Default to true, parent form already checks rights
    }
  },
  created() {
    // Check if parent route has view rights (for consistency)
    // Note: Parent TabulationSheetForm already checks rights before rendering this component
    const parentRouteName = 'tabulation-sheet'
    if (this.$store.state.rights && this.$store.state.rights[parentRouteName]) {
      this.hasViewRights = this.$store.state.rights[parentRouteName].view || false
    }
  },
  props: {
    classInfo: {
      type: String,
      default: 'N/A',
    },
    sectionInfo: {
      type: String,
      default: 'N/A',
    },
    subjectInfo: {
      type: String,
      default: 'N/A',
    },
    examTypeInfo: {
      type: String,
      default: 'N/A',
    },
    sessionInfo: {
      type: String,
      default: 'N/A',
    },
    type: {
      type: String,
      required: true,
    },
    typeDisplayName: {
      type: String,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    records: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sectionsMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    reportDomain() {
      return String(
        this.$store.state.report || this.$store.state.clientID || ""
      )
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
    },
    isHpgsDomain() {
      return (
        this.reportDomain === "myskoolhpgs" ||
        this.reportDomain === "hpgs" ||
        this.reportDomain.includes("hpgs")
      );
    },
    isWhssDomain() {
      return (
        this.reportDomain === "myskoolwhss" ||
        this.reportDomain === "whss" ||
        this.reportDomain.includes("whss")
      );
    },
    hidesTabulationTypePicker() {
      return this.isHpgsDomain || this.isWhssDomain;
    },
    isMadinatulIlmDomain() {
      return (
        this.reportDomain === "myskoolmadinatulilm" ||
        this.reportDomain.includes("madinatulilm")
      );
    },
    isSectionsMode() {
      return (
        this.sectionsMode ||
        (this.isMadinatulIlmDomain && this.type === "questions-base")
      );
    },
    isRemarksBaseSchool() {
      const domain = this.reportDomain;
      return (
        domain === "myskoolprofectus" ||
        domain.includes("profectus") ||
        domain === "myskool" ||
        this.isHpgsDomain
      );
    },
    totalMarks() {
      if (this.type !== 'questions-base') return 0
      return this.records.reduce((sum, record) => {
        return sum + (Number(record.totalMarks) || 0)
      }, 0)
    },
    resolvedTypeDisplayName() {
      if (this.isSectionsMode) return "Sections";
      return this.typeDisplayName;
    },
    remarksLanguageOptions() {
      return REMARKS_LANGUAGE_OPTIONS
    },
    classNamesList() {
      const info = (this.classInfo || "").trim();
      if (!info || info === "N/A" || !info.includes(",")) {
        return [];
      }
      return info
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    },
    sectionDisplay() {
      if (this.classNamesList.length > 0) {
        return "All";
      }
      return this.sectionInfo || "N/A";
    },
  },
}
</script>

<style lang="scss" scoped>
.tabulation-sheet-view {
  padding: 0;
}

// Header Card - Matching marksEntryStudent.vue style
.view-header-card {
  margin-bottom: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .view-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 2px solid #f0edff;
  }

  .title-wrapper {
    flex: 1;
    
    h4 {
      font-size: 1.25rem;
      font-weight: 800;
      color: #2d1b69;
      letter-spacing: -0.3px;
    }
    
    small {
      font-size: 0.85rem;
      color: #6b7280;
      font-weight: 500;
    }
  }
}

.view-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  .meta-item--class {
    grid-column: span 2;
  }

  .class-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.15rem;
  }

  .class-tag {
    font-weight: 600;
    font-size: 0.78rem;
  }

  .meta-item {
    background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
    border-radius: 14px;
    padding: 1rem 1.25rem;
    border: 1.5px solid #f0edff;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
      transform: scaleY(0);
      transition: transform 0.3s ease;
    }
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(99, 102, 241, 0.15);
      border-color: #d4c7ff;
      
      &::before {
        transform: scaleY(1);
      }
    }
    
    .label {
      display: block;
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.5rem;
    }
    
    .value {
      display: block;
      font-size: 1rem;
      font-weight: 700;
      color: #2d1b69;
    }
  }
}

// Content Wrapper
.view-content-wrapper {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e0d8ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

// Profectus remarks view — full-width stacked sections
.profectus-remarks-view {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
}

.profectus-section-block {
  width: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(115, 103, 240, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: #b8a9ff;
    box-shadow: 0 10px 32px rgba(115, 103, 240, 0.18);
  }
}

.profectus-section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  flex-wrap: wrap;

  h5 {
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #fff;
  }

  small {
    color: rgba(255, 255, 255, 0.85) !important;
    font-weight: 500;
  }
}

.profectus-section-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.view-lang-inline-group {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.view-lang-box {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 32px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
  line-height: 1;

  &.active {
    background: #fff;
    border-color: #fff;

    .view-lang-box-code {
      color: #7c5dfa;
    }

    .view-lang-box-label {
      color: #5e50a3;
    }
  }

  &.inactive {
    background: rgba(0, 0, 0, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
    opacity: 0.85;

    .view-lang-box-code,
    .view-lang-box-label {
      color: rgba(255, 255, 255, 0.65);
    }
  }
}

.view-lang-box-code {
  font-size: 0.75rem;
  font-weight: 800;
  color: #fff;
}

.view-lang-box-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}

.profectus-section-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profectus-section-title {
  flex: 1;
  min-width: 0;
}

.profectus-options-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.profectus-options-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;

  thead th {
    background: #f3f0ff;
    color: #5e50a3;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.9rem 1.25rem;
    border-bottom: 2px solid #e0d8ff;
    text-align: left;
  }

  tbody td {
    padding: 1rem 1.25rem;
    font-size: 0.95rem;
    color: #2d1b69;
    border-bottom: 1px solid #f0edff;
    vertical-align: top;
    line-height: 1.5;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:nth-child(even) {
    background: #faf9ff;
  }

  tbody tr:hover {
    background: #f3f0ff;
  }

  .col-sno {
    width: 72px;
    text-align: center;
    font-weight: 700;
    color: #7c5dfa;
    white-space: nowrap;
  }
}

.profectus-skeleton-row {
  margin: 0.75rem 1.25rem;
  border-radius: 8px;

  &:last-child {
    margin-bottom: 1.25rem;
  }
}

// Icon Base Styles
.view-icon-base-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
}

.view-category-card-modern {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(115, 103, 240, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;

    &::before {
      left: 100%;
    }

    .view-category-icon {
      box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
    }
  }
}

.view-category-header-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.category-divider {
  height: 2px;
  background: linear-gradient(90deg, #7c5dfa 0%, #cbb9ff 100%);
  border-radius: 2px;
  margin: 1rem 0;
}

.title-block {
  flex: 1;
  
  h6 {
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #2d1b69;
    margin-bottom: 0.15rem;
  }
  
  small {
    font-size: 0.75rem;
    color: #8a7fa8;
    font-weight: 500;
  }
}

.view-category-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f0ff;
  border-radius: 10px;
  color: #7c5dfa;
}

.view-category-title-modern {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  flex: 1;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option-row {
  background: #fff;
  border-radius: 16px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0;
  border: 1.5px solid #f0edff;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUpFade 0.5s ease-out both;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.15);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }

    .chip-index {
      box-shadow: 0 6px 18px rgba(124, 93, 250, 0.4);
      width: 38px !important;
      height: 38px !important;
    }
  }
  
  .option-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-height: 38px;
  }
}

.chip-index {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
}

.chip-label {
  font-size: 1rem;
  font-weight: 600;
  color: #2d1b69;
  flex: 1;
  line-height: 1.4;
}

// Questions Base Styles
.view-questions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.view-questions-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.view-summary-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.12);
  color: #2d1b69;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    color: #7c5dfa;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;
  }
}

.view-summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.view-summary-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.view-summary-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2d1b69;
}

.view-questions-list-modern {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.view-question-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.view-question-card-modern {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;

    &::before {
      transform: scaleY(1);
    }

    .view-question-number-badge {
      box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
    }
  }
}

.view-question-number-badge {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: white;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.4);
  transition: all 0.3s ease;
}

.view-question-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.view-question-title-inline {
  font-weight: 700;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.3;
}

.view-question-title {
  font-weight: 700;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.3;
}

.view-question-marks-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f3f0ff;
  color: #8b5cf6;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9375rem;
}

// Class Test Styles
.view-class-test-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.view-class-test-card-modern {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 30px rgba(115, 103, 240, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;

    &::before {
      left: 100%;
    }
  }
}

.view-test-header-modern {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1.25rem;
  border-bottom: 2px solid #f0edff;
}

.view-test-title-modern {
  font-size: 1.375rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.view-test-stats {
  display: flex;
  gap: 1.5rem;
}

.view-test-stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.view-test-stat-label {
  font-size: 0.875rem;
  color: #5a5c69;
  font-weight: 500;
}

.view-test-stat-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #7c5dfa;
  margin-left: auto;
}

// Empty States
.view-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6c757d;
  font-size: 0.9375rem;
  gap: 0.5rem;

  svg {
    opacity: 0.5;
  }
}

.view-empty-state-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: #6c757d;

  svg {
    opacity: 0.3;
    margin-bottom: 1rem;
  }

  h5 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9375rem;
    color: #6c757d;
    margin: 0;
  }
}

// Skeleton Loading Styles
.view-skeleton-container {
  padding: 0;
}

.view-header-card-skeleton {
  margin-bottom: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.75rem;
}

.skeleton-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 2px solid #f0edff;
}

.skeleton-title {
  height: 28px;
  width: 250px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 8px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.skeleton-badge {
  height: 24px;
  width: 100px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 12px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.skeleton-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.skeleton-meta-item {
  height: 80px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  border: 2px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.view-content-skeleton {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e0d8ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.skeleton-content-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.skeleton-content-item {
  flex: 1;
  height: 120px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 12px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  border: 2px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.skeleton-card {
  pointer-events: none;
}

.skeleton-category-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1.25rem;
  border-bottom: 2px solid #f0edff;
}

.skeleton-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 12px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  border: 2px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.skeleton-category-title {
  flex: 1;
  height: 24px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 8px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

.skeleton-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-option-row {
  height: 60px;
  background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 25%, #9ca3af 50%, #d1d5db 75%, #9ca3af 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;
  border-radius: 16px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  border: 2px solid #e5e7eb;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
    animation: skeletonShine 1.2s ease-in-out infinite;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

